import React, { useState, useEffect, useMemo } from "react";
import {
  collection,
  query,
  where,
  onSnapshot,
  doc,
  writeBatch,
  getDocs,
  getDoc,
} from "firebase/firestore";
import { db, functions } from "../services/firebase";
import { httpsCallable } from "firebase/functions";
import { ChevronRight } from "lucide-react";
import Layout from "../component/Layout";
import ProductImage from "../components/ProductImage";
import "./InWarehouse.css";
import bcrypt from "bcryptjs";
import PayModal from "./components/PayModal";
import {
  handleDispatchSellerPaymentWithJre,
  verifySellerPaymentHandover,
  resendSellerPaymentHandoverOtp,
  isSellerPaymentSettled,
  isSellerPaymentInFlight,
} from "./services/paymentService";
import PaymentDispatchIndicator from "../components/PaymentDispatchIndicator";
import OtpVerificationModal from "./components/OtpVerificationModal";
import {
  num,
  formatNumber,
  calculateWastage,
  calculateSetMc,
  calculateGramMcPerGram,
  calculateSpecMcPerGram,
  calculateOrderSummary,
} from "../utils/calculations";

const tableHeaderStyle = {
  padding: "10px 12px",
  textAlign: "left",
  fontSize: "10.5px",
  fontWeight: 700,
  letterSpacing: "0.05em",
  textTransform: "uppercase",
  // Theme gold (#8a6b32) to match the rest of the operator app.
  backgroundColor: "#8a6b32",
  color: "#ffffff",
  whiteSpace: "nowrap",
  borderBottom: "2px solid #6f5527",
  position: "sticky",
  top: 0,
  zIndex: 2,
};

const tableCellStyle = {
  padding: "10px 12px",
  fontSize: "12.5px",
  borderBottom: "1px solid #e2e8f0",
  color: "#0f172a",
  verticalAlign: "middle",
};

// 🔑 OTP Settings
const OTP_EXPIRY_MINUTES = 10;
const USE_CASE = "OPERATOR_STOCK_ISSUE_BUYER_JRE";

const getPaymentPreference = (order) =>
  order.paymentPreference ||
  order.sellerPaymentPreference ||
  order.orderDetail?.paymentPreference ||
  "BUYER_PAYMENT";

// Recognises settled payments on the new sellerPaymentStatus track
// (delivered / legacy verified / paid) via the shared helper.
const isSellerPaid = (order) => isSellerPaymentSettled(order);

const requiresWarehouseSellerPayment = (order) =>
  getPaymentPreference(order) === "WAREHOUSE" && !isSellerPaid(order);

const getWarehouseSellerPaymentGroups = (orders) => {
  const groups = {};
  orders
    .filter(requiresWarehouseSellerPayment)
    .forEach((order) => {
      const sellerId = order.sellerId || "UNKNOWN_SELLER";
      if (!groups[sellerId]) {
        groups[sellerId] = {
          groupId: sellerId,
          sellerId,
          operatorId: order.operatorId,
          sellerName: order.sellerName,
          orders: [],
        };
      }
      groups[sellerId].orders.push(order);
    });
  return Object.values(groups);
};

// Chevron Component
const Chevron = ({ open }) => (
  <ChevronRight
    size={16}
    style={{
      transition: "transform 0.15s ease",
      transform: open ? "rotate(90deg)" : "rotate(0deg)",
      color: "#475569",
    }}
  />
);

// Expandable Order Table with Size Details
const ExpandableOrderTable = ({ group }) => {
  const [expandedOrders, setExpandedOrders] = useState({});

  const toggleOrder = (orderId) => {
    setExpandedOrders(prev => ({ ...prev, [orderId]: !prev[orderId] }));
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return "—";
    const date = timestamp instanceof Date ? timestamp : new Date(timestamp);
    return date.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const inr = (n) =>
    `₹${Number(n).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;

  // Calculate size-level details
  const getSizeDetails = (order) => {
    const sizeDetails = [];
    const orderDetail = order.orderDetail || {};
    
    Object.entries(orderDetail).forEach(([size, variant]) => {
      // `orderDetail` stores PER-SET values plus a quantity, with the keys
      // `qty` / `netwt` / `grossWT` / `specWT`. Reading `quantity` / `netWt` /
      // `grossWt` resolved to undefined, so qty became 0 and every derived
      // column collapsed to zero.
      const qty = num(variant.qty ?? variant.quantity ?? variant.set) || 1;
      const netPerSet = num(variant.netwt ?? variant.netWt) || num(order.netWt);
      const grossPerSet = num(variant.grossWT ?? variant.grossWt) || num(order.grossWt);
      const specPerSet =
        num(variant.specWT ?? variant.specificationWt) ||
        Math.max(0, grossPerSet - netPerSet);
      const netWt = netPerSet;
      const grossWt = grossPerSet;
      const specWt = specPerSet;
      
      // Calculate fine weight for this size
      const purity = num(order.purity || 92);
      const wastage = calculateWastage(order);
      const fineWt = (netWt * qty * (purity + wastage)) / 100;
      
      // Calculate MC costs
      const setMc = calculateSetMc(order);
      const gramMcPerGram = calculateGramMcPerGram(order);
      const specMcPerGram = calculateSpecMcPerGram(order);
      
      const setMcTotal = setMc * qty;
      const gramMcTotal = netWt * qty * gramMcPerGram;
      const specMcTotal = specWt * qty * specMcPerGram;
      const mcTotal = setMcTotal + gramMcTotal + specMcTotal;
      
      // Calculate stamp
      const stampCost = num(order.stampCharges || 0);
      const stampQty = num(variant.stampCertificateQtyPerSet);
      const stampTotal = stampCost * (stampQty > 0 ? stampQty : qty);
      
      // Calculate amount
      const amount = mcTotal + stampTotal;
      
      sizeDetails.push({
        size,
        qty,
        grossWt: grossWt * qty,
        netWt: netWt * qty,
        specWt: specWt * qty,
        fineWt,
        mcTotal,
        mcPerGram: gramMcPerGram,
        stampTotal,
        stampCost,
        amount,
      });
    });
    
    return sizeDetails;
  };

  const thStyle = {
    ...tableHeaderStyle,
    position: "sticky",
    top: 0,
    zIndex: 1,
  };

  return (
    <div
      className="table-container"
      style={{
        border: "1px solid #e2e8f0",
        borderRadius: 12,
        overflow: "auto",
        maxHeight: "70vh",
        background: "#ffffff",
        boxShadow: "0 1px 3px rgba(15, 23, 42, 0.06)",
      }}
    >
      <table
        style={{
          // minWidth lets the wrapper scroll horizontally on narrow screens;
          // borderCollapse:separate is required for the sticky header.
          minWidth: 940,
          borderCollapse: "separate",
          borderSpacing: 0,
          tableLayout: "fixed",
        }}
      >
        <colgroup>
          <col style={{ width: "36px" }} /> {/* Chevron */}
          <col style={{ width: "48px" }} /> {/* Image */}
          <col style={{ minWidth: "140px" }} /> {/* Product */}
          <col style={{ width: "64px" }} /> {/* Qty */}
          <col style={{ width: "90px" }} /> {/* Gross Wt */}
          <col style={{ width: "90px" }} /> {/* Net Wt */}
          <col style={{ width: "90px" }} /> {/* Spec Wt */}
          <col style={{ width: "90px" }} /> {/* Fine Wt */}
          <col style={{ width: "100px" }} /> {/* MC Total */}
          <col style={{ width: "120px" }} /> {/* Stamp Total */}
          <col style={{ width: "100px" }} /> {/* Amount */}
          <col style={{ minWidth: "130px" }} /> {/* Order Time */}
        </colgroup>
        <thead>
          <tr style={{ backgroundColor: "#ffffff" }}>
            <th style={thStyle}></th>
            <th style={thStyle}>Img</th>
            <th style={{ ...thStyle, textAlign: "left" }}>Product</th>
            <th style={{ ...thStyle, textAlign: "right" }}>Qty</th>
            <th style={{ ...thStyle, textAlign: "right" }}>Gross Wt (g)</th>
            <th style={{ ...thStyle, textAlign: "right" }}>Net Wt (g)</th>
            <th style={{ ...thStyle, textAlign: "right" }}>Spec Wt (g)</th>
            <th style={{ ...thStyle, textAlign: "right" }}>Fine Wt (g)</th>
            <th style={{ ...thStyle, textAlign: "right" }}>MC Total</th>
            <th style={{ ...thStyle, textAlign: "right" }}>Stamp Total</th>
            <th style={{ ...thStyle, textAlign: "right" }}>Amount</th>
            <th style={{ ...thStyle, textAlign: "left" }}>Order Time</th>
          </tr>
        </thead>
        <tbody>
          {group.orders.map((order) => {
            const calc = calculateOrderSummary(order);
            const isExpanded = expandedOrders[order.docId];
            const sizeDetails = getSizeDetails(order);

            // Aggregate the per-size rows (per-set x qty). The order row is then
            // always exactly the sum of its size rows, so the two can never
            // disagree. Falls back to order-level fields when an order has no
            // size breakdown yet.
            const sum = (pick) =>
              sizeDetails.length
                ? sizeDetails.reduce((t, r) => t + num(r[pick]), 0)
                : null;

            const sets =
              sum("qty") ||
              order.selectedVariants?.reduce((s, v) => s + num(v.quantity), 0) ||
              1;
            const rowGross = sum("grossWt") ?? num(order.grossWt);
            const rowNet = sum("netWt") ?? num(order.netWt);
            const rowSpec = sum("specWt") ?? num(order.specificationWt);
            const rowFine = sum("fineWt") ?? num(calc.fine);
            const rowMc = sum("mcTotal") ?? num(calc.amount);
            const rowStamp = sum("stampTotal") ?? num(order.stampCharges) * sets;
            const rowAmount = sum("amount") ?? num(calc.amount);

            return (
              <React.Fragment key={order.docId}>
                {/* Main Order Row */}
                <tr
                  onClick={() => toggleOrder(order.docId)}
                  style={{
                    backgroundColor: isExpanded ? "#eef2ff" : "#ffffff",
                    borderBottom: "1px solid #e5e7eb",
                    cursor: "pointer",
                  }}
                  onMouseEnter={(e) => {
                    if (!isExpanded) e.currentTarget.style.backgroundColor = "#f1f5f9";
                  }}
                  onMouseLeave={(e) => {
                    if (!isExpanded) e.currentTarget.style.backgroundColor = "#ffffff";
                  }}
                >
                  <td style={{ ...tableCellStyle, textAlign: "center" }}>
                    <Chevron open={isExpanded} />
                  </td>
                  {/* Stop propagation so clicking the image opens the full-screen
                      viewer instead of toggling the size breakdown. */}
                  <td
                    style={{ ...tableCellStyle, textAlign: "center" }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <ProductImage
                      images={order.images}
                      productName={order.productName}
                      label={`${order.productName || "Product"} · ${order.docId?.slice(0, 8)}`}
                      size={40}
                    />
                  </td>
                  <td style={tableCellStyle}>
                    <div style={{ fontWeight: 600, fontSize: "13px" }}>
                      {order.productName || "—"}
                    </div>
                    <div style={{ fontSize: "10px", color: "#6b7280", marginTop: "2px" }}>
                      ID: {order.docId.substring(0, 8)}...
                    </div>
                  </td>
                  <td style={{ ...tableCellStyle, textAlign: "right" }}>{sets}</td>
                  <td style={{ ...tableCellStyle, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                    {rowGross.toFixed(3)}
                  </td>
                  <td style={{ ...tableCellStyle, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                    {rowNet.toFixed(3)}
                  </td>
                  <td style={{ ...tableCellStyle, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                    {rowSpec.toFixed(3)}
                  </td>
                  <td style={{ ...tableCellStyle, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                    {rowFine.toFixed(3)}
                  </td>
                  <td style={{ ...tableCellStyle, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                    {inr(rowMc)}
                  </td>
                  <td style={{ ...tableCellStyle, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                    {inr(rowStamp)}
                  </td>
                  <td style={{ ...tableCellStyle, textAlign: "right", fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>
                    {inr(rowAmount)}
                  </td>
                  <td style={{ ...tableCellStyle, color: "#475569", fontSize: "11px" }}>
                    {formatTimestamp(order.orderTime)}
                  </td>
                </tr>

                {/* Size Details Rows */}
                {isExpanded && sizeDetails.length > 0 && (
                  <tr>
                    <td colSpan={12} style={{ padding: 0, border: "none" }}>
                      <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
                        <colgroup>
                          <col style={{ width: "36px" }} />
                          <col style={{ width: "48px" }} />
                          <col style={{ minWidth: "140px" }} />
                          <col style={{ width: "64px" }} />
                          <col style={{ width: "90px" }} />
                          <col style={{ width: "90px" }} />
                          <col style={{ width: "90px" }} />
                          <col style={{ width: "90px" }} />
                          <col style={{ width: "100px" }} />
                          <col style={{ width: "120px" }} />
                          <col style={{ width: "100px" }} />
                          <col style={{ minWidth: "130px" }} />
                        </colgroup>
                        <thead>
                          <tr style={{ backgroundColor: "#eef0fa" }}>
                            <th style={{ ...thStyle, fontSize: "10px" }}></th>
                            <th style={{ ...thStyle, fontSize: "10px" }}></th>
                            <th style={{ ...thStyle, fontSize: "10px", textAlign: "left" }}>Size</th>
                            <th style={{ ...thStyle, fontSize: "10px", textAlign: "right" }}>Qty</th>
                            <th style={{ ...thStyle, fontSize: "10px", textAlign: "right" }}>Gross (g)</th>
                            <th style={{ ...thStyle, fontSize: "10px", textAlign: "right" }}>Net (g)</th>
                            <th style={{ ...thStyle, fontSize: "10px", textAlign: "right" }}>Spec (g)</th>
                            <th style={{ ...thStyle, fontSize: "10px", textAlign: "right" }}>Fine (g)</th>
                            <th style={{ ...thStyle, fontSize: "10px", textAlign: "right" }}>
                              <div style={{ fontSize: "9px", color: "#6b7280" }}>MC (₹/g)</div>
                              <div>MC Total</div>
                            </th>
                            <th style={{ ...thStyle, fontSize: "10px", textAlign: "right" }}>
                              <div style={{ fontSize: "9px", color: "#6b7280" }}>Stamp (₹/set)</div>
                              <div>Stamp Total</div>
                            </th>
                            <th style={{ ...thStyle, fontSize: "10px", textAlign: "right" }}>Amount</th>
                            <th style={{ ...thStyle, fontSize: "10px" }}></th>
                          </tr>
                        </thead>
                        <tbody>
                          {sizeDetails.map((detail, index) => (
                            <tr key={index} style={{ backgroundColor: "#f7f8ff" }}>
                              <td style={{ ...tableCellStyle, borderBottom: "1px solid #e8ebf4" }}></td>
                              <td style={{ ...tableCellStyle, borderBottom: "1px solid #e8ebf4" }}></td>
                              <td style={{ ...tableCellStyle, borderBottom: "1px solid #e8ebf4", fontWeight: 700 }}>
                                {detail.size}
                              </td>
                              <td style={{ ...tableCellStyle, borderBottom: "1px solid #e8ebf4", textAlign: "right" }}>
                                {detail.qty}
                              </td>
                              <td style={{ ...tableCellStyle, borderBottom: "1px solid #e8ebf4", textAlign: "right" }}>
                                {detail.grossWt.toFixed(3)}
                              </td>
                              <td style={{ ...tableCellStyle, borderBottom: "1px solid #e8ebf4", textAlign: "right" }}>
                                {detail.netWt.toFixed(3)}
                              </td>
                              <td style={{ ...tableCellStyle, borderBottom: "1px solid #e8ebf4", textAlign: "right" }}>
                                {detail.specWt.toFixed(3)}
                              </td>
                              <td style={{ ...tableCellStyle, borderBottom: "1px solid #e8ebf4", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                                {detail.fineWt.toFixed(3)}
                              </td>
                              <td style={{ ...tableCellStyle, borderBottom: "1px solid #e8ebf4", textAlign: "right" }}>
                                <div style={{ fontSize: "9px", color: "#6b7280" }}>
                                  ₹{detail.mcPerGram.toFixed(2)}/g
                                </div>
                                <div style={{ fontVariantNumeric: "tabular-nums", fontWeight: 600, fontSize: "11px" }}>
                                  {inr(detail.mcTotal)}
                                </div>
                              </td>
                              <td style={{ ...tableCellStyle, borderBottom: "1px solid #e8ebf4", textAlign: "right" }}>
                                <div style={{ fontSize: "9px", color: "#6b7280" }}>
                                  ₹{detail.stampCost.toFixed(2)}/set
                                </div>
                                <div style={{ fontVariantNumeric: "tabular-nums", fontSize: "10px" }}>
                                  {detail.qty} × ₹{detail.stampCost.toFixed(2)} = {inr(detail.stampTotal)}
                                </div>
                              </td>
                              <td style={{ ...tableCellStyle, borderBottom: "1px solid #e8ebf4", textAlign: "right", fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>
                                {inr(detail.amount)}
                              </td>
                              <td style={{ ...tableCellStyle, borderBottom: "1px solid #e8ebf4" }}></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const InWarehouse = () => {
  const [groupedOrders, setGroupedOrders] = useState({});
  const [loading, setLoading] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const operatorId = localStorage.getItem("operatorId");

  // Modal state
  const [verifyModal, setVerifyModal] = useState(null);
  const [selectedPayGroup, setSelectedPayGroup] = useState(null);
  const [handoverOtp, setHandoverOtp] = useState(null);
  const [otpInput, setOtpInput] = useState("");
  const [otpError, setOtpError] = useState("");

  // 🔍 Find active buyerside JRE
  const findActiveJreWithMobile = async (operatorId) => {
    try {
      const jreStatusQuery = query(
        collection(db, "jreStatus"),
        where("role", "==", "buyerside"),
        where("operatorId", "==", operatorId),
        where("isOnline", "==", true),
      );
      const statusSnapshot = await getDocs(jreStatusQuery);
      if (statusSnapshot.empty) return null;

      const jreStatusDoc = statusSnapshot.docs[0];
      const jreId = jreStatusDoc.id;
      const jreStatusData = jreStatusDoc.data();

      const jreRegDoc = await getDoc(doc(db, "jreregistrations", jreId));
      const primaryMobile = jreRegDoc.exists()
        ? jreRegDoc.data().primaryMobile?.toString().trim() || null
        : null;

      return {
        id: jreId,
        ...jreStatusData,
        primaryMobile,
      };
    } catch (err) {
      console.error("Error fetching JRE:", err);
      return null;
    }
  };

  useEffect(() => {
    // Superadmin sees In-Warehouse orders across ALL operators.
    const orderListRef = collection(db, "orderList");
    const q = query(
      orderListRef,
      where("orderStatus", "==", "InWarehouse"),
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const ordersData = [];
        snapshot.forEach((docSnap) => {
          const data = docSnap.data();
          let orderTime = data.orderTime?.seconds
            ? new Date(data.orderTime.seconds * 1000)
            : data.addedAt
              ? new Date(data.addedAt)
              : null;
          ordersData.push({ docId: docSnap.id, ...data, orderTime });
        });

        const groups = {};
        ordersData.forEach((order) => {
          const buyer = order.buyerId || "Unknown Buyer";
          if (!groups[buyer]) groups[buyer] = { buyer, orders: [] };
          groups[buyer].orders.push(order);
        });

        Object.keys(groups).forEach((key) => {
          groups[key].orders.sort(
            (a, b) => (a.orderTime || 0) - (b.orderTime || 0),
          );
        });

        setGroupedOrders(groups);
      },
      (error) => {
        console.error("Firestore snapshot listener error:", error);
      },
    );

    return () => unsubscribe();
  }, []);

  const calculateGroupTotals = (orders) => {
    let totalSets = 0,
      totalGrossWt = 0,
      totalNetWt = 0,
      totalFine = 0,
      totalAmount = 0;

    orders.forEach((order) => {
      const summary = calculateOrderSummary(order);
      totalSets += summary.sets;
      totalGrossWt += summary.gross;
      totalNetWt += summary.net;
      totalFine += summary.fine;
      totalAmount += summary.amount;
    });

    return {
      totalSets: formatNumber(totalSets, 3),
      totalGrossWt: formatNumber(totalGrossWt, 3),
      totalNetWt: formatNumber(totalNetWt, 3),
      totalFine: formatNumber(totalFine, 3),
      totalAmount: formatNumber(totalAmount, 2),
    };
  };

  // 🖨️ Print (assign JRE if needed)
  const handlePrint = async (buyer, group) => {
    if (loading) return;
    if (group.orders.some(requiresWarehouseSellerPayment)) {
      alert("Seller payment is required before dispatch for At Warehouse payment orders.");
      return;
    }
    const currentOperatorId = group.orders[0]?.operatorId;
    if (!currentOperatorId) {
      alert("Operator ID missing.");
      return;
    }

    setLoading(true);
    try {
      let jre = null;
      if (!group.orders[0].jreId) {
        jre = await findActiveJreWithMobile(currentOperatorId);
        if (!jre) {
          alert(
            `❌ No active buyerside JRE for operator: ${currentOperatorId}`,
          );
          return;
        }
        if (!jre.primaryMobile) {
          alert(`❌ JRE ${jre.id} has no mobile.`);
          return;
        }

        const cleanMobile = jre.primaryMobile.replace(/\D/g, "");
        if (cleanMobile.length !== 10) {
          alert(`❌ Invalid mobile: ${jre.primaryMobile}`);
          return;
        }

        // Assign JRE
        const batch = writeBatch(db);
        group.orders.forEach((order) => {
          batch.update(doc(db, "orderList", order.docId), {
            jreId: jre.id,
            assignedAt: new Date(),
          });
        });
        await batch.commit();

        // Refresh UI to show both buttons
        setGroupedOrders((prev) => {
          const updated = { ...prev };
          updated[buyer] = {
            ...updated[buyer],
            orders: updated[buyer].orders.map((o) => ({ ...o, jreId: jre.id })),
          };
          return updated;
        });
      } else {
        jre = { id: group.orders[0].jreId };
      }

      // Print note
      const groupTotals = calculateGroupTotals(group.orders);
      const currentDate = new Date()
        .toLocaleDateString("en-IN", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        })
        .toUpperCase();

      const printContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>JEWELMARTINDIA - ${buyer}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 10px; max-width: 1000px; font-size: 12px; }
            .header { text-align: center; margin-bottom: 15px; border-bottom: 2px solid #000; padding-bottom: 10px; }
            .company-name { font-size: 24px; font-weight: bold; margin-bottom: 5px; }
            table { width: 100%; border-collapse: collapse; margin-bottom: 15px; }
            th, td { border: 1px solid #000; padding: 6px 4px; text-align: left; font-size: 11px; }
            th { background-color: #f0f0f0; font-weight: bold; }
            .product-details { font-size: 10px; color: #555; margin-top: 2px; line-height: 1.3; }
            .total-row { background-color: #f0f0f0; font-weight: bold; }
            .footer { margin-top: 20px; text-align: center; font-size: 10px; color: #666; border-top: 1px solid #000; padding-top: 10px; }
            @media print { body { margin: 0; } }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="company-name">JEWELMARTINDIA</div>
            <div>NOTE NO: ${Math.floor(Math.random() * 100) + 1}</div>
            <div>DATE: ${currentDate}</div>
            <div>DELIVERY NOTE</div>
          </div>

          <table>
            <thead>
              <tr>
                <th>SL.NO.</th>
                <th>PARTICULARS</th>
                <th>SETS</th>
                <th>GROSS WT.</th>
                <th>NET WT.</th>
                <th>FINE</th>
                <th>AMOUNT</th>
              </tr>
            </thead>
            <tbody>
              ${group.orders
                .map((order, index) => {
                  const calc = calculateOrderSummary(order);
                  const setMc = calculateSetMc(order);
                  const gramMcPerGram = calculateGramMcPerGram(order);
                  const specMcPerGram = calculateSpecMcPerGram(order);
                  const wastage = calculateWastage(order);
                  const sets =
                    order.selectedVariants?.reduce(
                      (sum, v) => sum + num(v.quantity),
                      0,
                    ) || 1;
                  const sizes = order.orderDetail
                    ? Object.keys(order.orderDetail).join(", ")
                    : "Regular";
                  return `
                  <tr>
                    <td>${index + 1}</td>
                    <td>
                      <div><strong>${order.category}, ${order.productSource}, ${
                        order.productName
                      }</strong></div>
                      <div class="product-details">
                        purity:${order.purity || "92"}, specification:${
                          order.specification || "Plane"
                        }, size:${sizes}<br>
                        westage(${wastage.toFixed(2)})<br>
                        setmc(${setMc.toFixed(2)})<br>
                        netgrammc(${order.netWt || "0"} * ${gramMcPerGram.toFixed(
                          2,
                        )})<br>
                        ${
                          order.specificationWt &&
                          num(order.specificationWt) > 0
                            ? `spec.gmrate(${
                                order.specificationWt || "0"
                              } * ${specMcPerGram.toFixed(2)})`
                            : ""
                        }
                      </div>
                    </td>
                    <td>${sets}</td>
                    <td>${num(order.grossWt).toFixed(3)}</td>
                    <td>${num(order.netWt).toFixed(3)}</td>
                    <td>${calc.fine}</td>
                    <td>${calc.amount.toFixed(2)}</td>
                  </tr>
                `;
                })
                .join("")}
              <tr class="total-row">
                <td colspan="2">TOTAL</td>
                <td>${groupTotals.totalSets}</td>
                <td>${groupTotals.totalGrossWt}</td>
                <td>${groupTotals.totalNetWt}</td>
                <td>${groupTotals.totalFine}</td>
                <td>${groupTotals.totalAmount}</td>
              </tr>
            </tbody>
          </table>

          <div class="footer">
            THIS NOTE IS ONLY FOR DETAILING PURPOSE FOR EACH PRODUCT
          </div>

          <script>
            window.onload = function() {
              if (!window.location.search.includes('no-print')) {
                window.print();
                setTimeout(() => window.close(), 1000);
              }
            }
          </script>
        </body>
        </html>
      `;

      const printWindow = window.open("?no-print", "_blank");
      if (printWindow) {
        printWindow.document.write(printContent);
        printWindow.document.close();
      }

      setAlertMessage(`✅ Printed for JRE ${jre.id}`);
      setTimeout(() => setAlertMessage(""), 4000);
    } catch (err) {
      console.error("Print error:", err);
      alert(`❌ Failed: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // 🔐 Trigger OTP via sendOTP cloud function
  const handleVerifyJre = async (buyer, group) => {
    if (group.orders.some(requiresWarehouseSellerPayment)) {
      alert("Seller payment is required before dispatch for At Warehouse payment orders.");
      return;
    }
    const jreId = group.orders[0].jreId;
    const jreStatusDoc = await getDoc(doc(db, "jreStatus", jreId));
    if (!jreStatusDoc.exists()) {
      alert(`JRE ${jreId} not found.`);
      return;
    }

    const jre = { id: jreId, ...jreStatusDoc.data() };
    const jreRegDoc = await getDoc(doc(db, "jreregistrations", jreId));
    const primaryMobile = jreRegDoc.exists()
      ? jreRegDoc.data().primaryMobile?.toString().trim() || null
      : null;

    if (!primaryMobile) {
      alert(`JRE ${jreId} has no mobile.`);
      return;
    }

    const cleanMobile = primaryMobile.replace(/\D/g, "");
    if (cleanMobile.length !== 10) {
      alert(`Invalid mobile for JRE ${jreId}`);
      return;
    }

    const totalValue = group.orders.reduce(
      (sum, order) => sum + calculateOrderSummary(order).amount,
      0,
    );
    const currentValue = jre.currentValue ?? 0;
    const maxValue = jre.maxValue ?? Infinity;

    if (currentValue + totalValue > maxValue) {
      alert(`❌ JRE ${jreId} limit exceeded!`);
      return;
    }

    setLoading(true);
    try {
      const sendOTP = httpsCallable(functions, "sendOTP");
      const result = await sendOTP({
        templateName: USE_CASE,
        mobile: cleanMobile,
        useCase: USE_CASE,
        validity: OTP_EXPIRY_MINUTES,
        params: {
          OPERATOR: operatorId,
        },
      });

      const otpId = result.data.otpId;

      // Open modal
      setVerifyModal({ buyer, group, otpId, jre, totalValue });
      setOtpInput("");
      setOtpError("");
    } catch (err) {
      console.error("OTP error:", err);
      alert(`❌ OTP generation failed: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // ✅ Verify entered OTP
  const verifyOtp = async () => {
    if (!verifyModal) return;
    if (otpInput.length !== 6 || !/^\d{6}$/.test(otpInput)) {
      setOtpError("Please enter a valid 6-digit OTP");
      return;
    }

    setLoading(true);
    setOtpError("");

    try {
      const { otpId, buyer, group, jre, totalValue } = verifyModal;
      const otpRef = doc(db, "otps", otpId);
      const otpSnap = await getDoc(otpRef);

      if (!otpSnap.exists()) {
        setOtpError("OTP not found or expired.");
        return;
      }

      const data = otpSnap.data();
      const now = new Date();

      if (data.expiresAt && now > data.expiresAt.toDate()) {
        setOtpError("OTP has expired.");
        return;
      }

      if (data.status === "verified") {
        setOtpError("OTP already used.");
        return;
      }

      const isValid = await bcrypt.compare(otpInput, data.otp);
      if (!isValid) {
        setOtpError("Invalid OTP.");
        return;
      }

      // ✅ Dispatch
      const batch = writeBatch(db);
      batch.update(otpRef, { status: "verified", verifiedAt: new Date() });

      group.orders.forEach((order) => {
        batch.update(doc(db, "orderList", order.docId), {
          orderStatus: "Dispatched",
          dispatchedAt: new Date(),
          otpReference: otpId,
        });
      });

      batch.update(doc(db, "jreStatus", jre.id), {
        currentValue: (jre.currentValue || 0) + totalValue,
      });

      await batch.commit();

      // Remove from UI
      setGroupedOrders((prev) => {
        const updated = { ...prev };
        delete updated[buyer];
        return updated;
      });

      setVerifyModal(null);
      setAlertMessage(`✅ Dispatched to JRE ${jre.id}`);
      setTimeout(() => setAlertMessage(""), 4000);
    } catch (err) {
      console.error("Verification error:", err);
      setOtpError("Verification failed.");
    } finally {
      setLoading(false);
    }
  };

  const closeModal = () => {
    setVerifyModal(null);
    setOtpInput("");
    setOtpError("");
  };

  const handleWarehousePaymentConfirm = async (settlementPayload) => {
    if (!selectedPayGroup) return;
    setLoading(true);
    try {
      const result = await handleDispatchSellerPaymentWithJre(
        selectedPayGroup.groupId,
        selectedPayGroup,
        setAlertMessage,
        settlementPayload,
        { stage: "WAREHOUSE" },
      );
      setSelectedPayGroup(null);
      // Assign step complete — the payment group now shows "Assigned" with a
      // "Verify JRE" switch. The handover OTP has been issued; the operator
      // verifies it via that switch (instead of auto-opening the modal).
      setAlertMessage(
        `✅ Payment assigned to JRE ${result.jreId}. Use "Verify JRE" to confirm handover.`,
      );
      setTimeout(() => setAlertMessage(""), 5000);
    } catch (err) {
      console.error("Warehouse seller payment error:", err);
      alert(`❌ Payment dispatch failed: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

// Re-open the handover OTP step for a payment already assigned to a JRE
// (e.g. verification modal closed early or OTP expired). Re-issues the OTP.
const resumeSellerPaymentHandover = async (paymentGroup) => {
  setLoading(true);
  try {
    const data = await resendSellerPaymentHandoverOtp({
      ...paymentGroup,
      jreId:
        paymentGroup.orders[0]?.sellerPaymentJreId ||
        paymentGroup.orders[0]?.jreId,
    });
    setHandoverOtp(data);
  } catch (err) {
    console.error("Resume seller payment handover error:", err);
    alert(`❌ Could not re-issue handover OTP: ${err.message}`);
  } finally {
    setLoading(false);
  }
};

  const groupEntries = useMemo(
    () => Object.entries(groupedOrders),
    [groupedOrders],
  );

  return (
    <Layout pageTitle="Orders In Warehouse">
      {alertMessage && (
        <div
          style={{
            backgroundColor: "#d4edda",
            color: "#155724",
            padding: "10px",
            textAlign: "center",
            marginBottom: "10px",
            borderRadius: "4px",
            fontWeight: "bold",
          }}
        >
          {alertMessage}
        </div>
      )}

      <div className="container">
        {groupEntries.length === 0 ? (
          <p className="no-orders">No orders in warehouse.</p>
        ) : (
	          groupEntries.map(([buyer, group]) => {
	            const isAssigned = !!group.orders[0]?.jreId;
	            const groupTotals = calculateGroupTotals(group.orders);
	            const warehousePaymentGroups = getWarehouseSellerPaymentGroups(
	              group.orders,
	            );
	            const hasBlockedWarehousePayment =
	              warehousePaymentGroups.length > 0;

            return (
              <div
                key={buyer}
                style={{
                  marginBottom: "28px",
                  border: "2px solid #1f2937",
                  borderRadius: "8px",
                  overflow: "hidden",
                }}
              >
                {/* Buyer summary row */}
                <div
                  style={{
                    // Theme gold to match the rest of the operator app.
                    backgroundColor: "#8a6b32",
                    color: "#ffffff",
                    padding: "10px 14px",
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "12px 16px",
                    alignItems: "center",
                    justifyContent: "space-between",
                    borderTopLeftRadius: 12,
                    borderTopRightRadius: 12,
                  }}
                  onClick={() => {}}
                >
                  <strong style={{ fontSize: "16px" }}>Buyer: {buyer}</strong>
                  <span>Orders: {group.orders.length}</span>
                  <span>Sets: {groupTotals.totalSets}</span>
                  <span>Gross: {groupTotals.totalGrossWt}g</span>
                  <span>Net: {groupTotals.totalNetWt}g</span>
                  <span>Amount: ₹{groupTotals.totalAmount}</span>
                  {isAssigned && (
                    <span style={{ color: "#17a2b8", marginLeft: "auto" }}>
                      <strong>Assigned JRE:</strong> {group.orders[0].jreId}
                    </span>
                  )}
                    {/* Payment Dispatch Indicator — compact, fits inline in the header. */}
                    <div
                      style={{
                        marginLeft: "auto",
                        width: "440px",
                        maxWidth: "100%",
                      }}
                    >
                      <PaymentDispatchIndicator orders={group.orders} stage="WAREHOUSE" />
                    </div>
	                  <div className="action-buttons">
	                    {warehousePaymentGroups.filter((pg) => pg.orders.some(isSellerPaymentInFlight)).map((paymentGroup) => (
                        <button
                          key={`resume-${paymentGroup.groupId}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            resumeSellerPaymentHandover(paymentGroup);
                          }}
                          className="print-btn"
                          disabled={loading}
                          title="Payment already assigned to a JRE — re-issue handover OTP to verify."
                        >
                          🔐 Verify JRE {paymentGroup.groupId}
                        </button>
                      ))}
                      {warehousePaymentGroups.filter((pg) => !pg.orders.some(isSellerPaymentInFlight)).map((paymentGroup) => (
	                      <button
	                        key={paymentGroup.groupId}
	                        onClick={(e) => {
	                          e.stopPropagation();
	                          setSelectedPayGroup(paymentGroup);
	                        }}
	                        className="print-btn"
	                        disabled={loading}
	                        title="Seller payment must be delivered and OTP-confirmed before warehouse dispatch."
	                      >
	                        Pay Seller {paymentGroup.groupId}
	                      </button>
	                    ))}
	                    <button
	                      onClick={() => handlePrint(buyer, group)}
	                      className="print-btn"
	                      disabled={loading || hasBlockedWarehousePayment}
	                      title={
	                        hasBlockedWarehousePayment
	                          ? "Seller payment required before dispatch."
	                          : ""
	                      }
	                    >
	                      {loading ? "Dispatching..." : " Dispatch"}
	                    </button>
	                    {isAssigned && (
	                      <button
	                        onClick={() => handleVerifyJre(buyer, group)}
	                        className="verify-btn"
	                        disabled={loading || hasBlockedWarehousePayment}
	                        title={
	                          hasBlockedWarehousePayment
	                            ? "Seller payment required before dispatch."
	                            : ""
	                        }
	                      >
	                        {loading ? "Sending OTP..." : "🔐 Verify JRE"}
	                      </button>
	                    )}
	                  </div>
                </div>

                <ExpandableOrderTable group={group} />
              </div>
            );
          })
        )}
      </div>

      {verifyModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0,0,0,0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1000,
          }}
        >
          <div
            style={{
              backgroundColor: "white",
              padding: "20px",
              borderRadius: "8px",
              width: "350px",
              textAlign: "center",
            }}
          >
            <h3>Verify JRE Handover</h3>
            <p>Enter the 6-digit OTP received from JRE</p>
            <input
              type="text"
              value={otpInput}
              onChange={(e) => {
                const val = e.target.value.replace(/\D/g, "").slice(0, 6);
                setOtpInput(val);
                setOtpError("");
              }}
              placeholder="Enter OTP"
              maxLength={6}
              style={{
                width: "100%",
                padding: "10px",
                margin: "15px 0",
                border: "1px solid #ccc",
                borderRadius: "4px",
                fontSize: "18px",
                textAlign: "center",
              }}
            />
            {otpError && (
              <div style={{ color: "red", fontSize: "14px" }}>{otpError}</div>
            )}
            <div style={{ marginTop: "20px" }}>
              <button
                onClick={closeModal}
                disabled={loading}
                style={{
                  backgroundColor: "#6c757d",
                  color: "white",
                  border: "none",
                  padding: "10px 20px",
                  borderRadius: "4px",
                  marginRight: "10px",
                }}
              >
                Cancel
              </button>
              <button
                onClick={verifyOtp}
                disabled={loading || otpInput.length !== 6}
                style={{
                  backgroundColor: "#28a745",
                  color: "white",
                  border: "none",
                  padding: "10px 20px",
                  borderRadius: "4px",
                }}
              >
                {loading ? "Verifying..." : "Verify"}
              </button>
            </div>
          </div>
        </div>
      )}
      {selectedPayGroup && (
        <PayModal
          seller={selectedPayGroup.groupId}
          group={selectedPayGroup}
          onClose={() => setSelectedPayGroup(null)}
          onConfirm={handleWarehousePaymentConfirm}
        />
      )}

      {handoverOtp && (
        <OtpVerificationModal
          modalData={handoverOtp}
          onClose={() => setHandoverOtp(null)}
          onVerify={(msg) => {
            setAlertMessage(msg);
            setTimeout(() => setAlertMessage(""), 6000);
          }}
          verifyFn={verifySellerPaymentHandover}
          resendFn={resendSellerPaymentHandoverOtp}
        />
      )}
    </Layout>
  );
};

export default InWarehouse;
